"use client";

import clsx from "clsx";
import React, {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
} from "react";
import { twMerge } from "tailwind-merge";
import Spinner from "./Spinner";
import Typography, { TypeScale } from "./Typography";

export const BUTTON_STYLE = {
  xs: {
    height: "h-5",
    width: "w-5",
    radius: "rounded-[5px]",
    padding: "px-1.5 py-1",
    gap: "gap-1",
    gapOffsetForTrailingIconWithAlignStart: "-ml-1",
    icon: "w-3 h-3",
    iconOnly: "w-[14px] h-[14px]",
  },
  s: {
    height: "h-7",
    width: "w-7",
    radius: "rounded-[7px]",
    padding: "px-2 py-1.5",
    gap: "gap-1.5",
    gapOffsetForTrailingIconWithAlignStart: "-ml-1.5",
    icon: "w-3.5 h-3.5",
    iconOnly: "w-[16px] h-[16px]",
  },
  m: {
    height: "h-9",
    width: "w-9",
    radius: "rounded-[10px]",
    padding: "px-3 py-2",
    gap: "gap-2",
    gapOffsetForTrailingIconWithAlignStart: "-ml-2",
    icon: "w-4 h-4",
    iconOnly: "w-[18px] h-[18px]",
  },
  l: {
    height: "h-11",
    width: "w-11",
    radius: "rounded-xl",
    padding: "px-4 py-2.5",
    gap: "gap-2",
    icon: "w-4 h-4",
    iconOnly: "w-6 h-6",
    gapOffsetForTrailingIconWithAlignStart: "-ml-2.5",
  },
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "xs" | "s" | "m" | "l";
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "flat"
    | "glass"
    | "ghost"
    | "danger";
  loading?: boolean;
  loadingLabel?: string;
  align?: "center" | "start";
  asChild?: boolean;
}

type ButtonContextValue = {
  size: NonNullable<ButtonProps["size"]>;
  variant: NonNullable<ButtonProps["variant"]>;
  align: NonNullable<ButtonProps["align"]>;
  hasText: boolean;
};

const ButtonContext = createContext<ButtonContextValue | null>(null);

const useButtonContext = (): ButtonContextValue => {
  const ctx = useContext(ButtonContext);
  if (!ctx)
    throw new Error("Button subcomponents must be used within <Button>");
  return ctx;
};

const _variant: {
  [key in NonNullable<ButtonProps["variant"]>]: HTMLElement["className"];
} = {
  primary:
    "bg-foreground border-foreground text-background stroke-background duration-100 hover:bg-foreground-active aria-[expanded=true]:bg-foreground-active aria-[expanded=true]:opacity-100",
  secondary:
    "border border-border text-foreground bg-background dark:bg-surface aria-[expanded=true]:opacity-100",
  flat: "bg-surface-secondary hover:bg-surface-tertiary aria-[expanded=true]:bg-slate-300 dark:aria-[expanded=true]:bg-surface-secondary text-foreground border-transparent aria-[expanded=true]:opacity-100",
  glass:
    "hover:bg-white/30 aria-[expanded=true]:bg-white/30 bg-white/20 backdrop-blur-sm text-white border-transparent aria-[expanded=true]:opacity-100",
  // For buttons overlaid on media: invisible at rest, subtle tint on hover.
  ghost:
    "bg-transparent hover:bg-white/10 aria-[expanded=true]:bg-white/10 text-white border-transparent aria-[expanded=true]:opacity-100",
  danger:
    "bg-white border-destructive text-destructive hover:bg-opacity-80 aria-[expanded=true]:bg-opacity-80 aria-[expanded=true]:opacity-100",
  tertiary:
    "text-foreground border-white/0 bg-transparent hover:bg-surface-secondary aria-[expanded=true]:bg-surface-secondary dark:border-transparent dark:hover:bg-surface dark:aria-[expanded=true]:bg-surface [&_button.tertiary:hover]:bg-surface-secondary aria-[expanded=true]:opacity-100",
};

const _textWeightVariant: {
  [key in NonNullable<ButtonProps["variant"]>]: HTMLElement["className"];
} = {
  primary: "!font-normal dark:!font-medium",
  secondary: "font-medium dark:!font-normal",
  tertiary: "font-medium dark:!font-normal",
  flat: "font-medium dark:!font-normal",
  glass: "font-normal",
  ghost: "font-normal",
  danger: "font-normal",
};

const _typographyVariant: {
  [k in NonNullable<ButtonProps["size"]>]: TypeScale;
} = {
  xs: "button3",
  s: "button2",
  m: "button1",
  l: "button1",
};

type ButtonComponent = React.ForwardRefExoticComponent<
  ButtonProps & React.RefAttributes<HTMLButtonElement>
> & {
  Icon: React.FC<React.PropsWithChildren<unknown>>;
  Text: React.FC<React.PropsWithChildren<unknown>>;
  Content: React.FC<React.PropsWithChildren<unknown>>;
};

const ButtonText: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { variant, size, align } = useButtonContext();
  return (
    <Typography
      variant={_typographyVariant[size]}
      className={clsx(
        "whitespace-nowrap truncate text-start",
        _textWeightVariant[variant],
        align === "start" && "mr-auto",
      )}
    >
      {children}
    </Typography>
  );
};

const ButtonIcon: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { size, hasText } = useButtonContext();
  if (!isValidElement(children)) return null;
  const sizeClass = hasText
    ? BUTTON_STYLE[size].icon
    : BUTTON_STYLE[size].iconOnly;
  const childEl = children as React.ReactElement<any>;
  const existing = (childEl.props as { className?: string }).className || "";
  const iconElement = cloneElement(childEl, {
    className: clsx(existing, sizeClass, "shrink-0"),
    "aria-hidden": true,
  } as any);
  return iconElement;
};

const ButtonContent: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  return <>{children}</>;
};

const ButtonRoot = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      size = "m",
      variant = "primary",
      align = "center",
      loading = false,
      loadingLabel,
      className,
      children,
      disabled,
      ...rest
    },
    ref,
  ) => {
    let asChildElement: React.ReactElement<any> | null = null;
    let contentChildren: React.ReactNode;

    if (asChild) {
      const only = Children.only(children);
      asChildElement = only as React.ReactElement<any>;
      contentChildren = (asChildElement.props as any).children;
    } else {
      contentChildren = children;
    }

    const childArray = Children.toArray(contentChildren);

    const analyzedChildren = childArray.map((child) => {
      if (typeof child === "string" || typeof child === "number") {
        return { child, type: "text" as const };
      }
      if (isValidElement(child)) {
        const type = (child as any).type;
        if (type === ButtonText) return { child, type: "text" as const };
        if (type === ButtonContent) return { child, type: "content" as const };
        return { child, type: "icon" as const };
      }
      return { child, type: "text" as const };
    });

    const hasText = analyzedChildren.some((c) => c.type === "text");
    const lastIndex = analyzedChildren.length - 1;
    const isTrailingIcon =
      lastIndex >= 0 && analyzedChildren[lastIndex].type === "icon";

    const _padding = () => {
      if (hasText && align == "center") return BUTTON_STYLE[size].padding;

      if (hasText && align == "start" && isTrailingIcon) {
        const alignStartPaddind = {
          xs: "pr-0",
          s: "pr-0",
          m: "pr-0",
          l: "pr-0",
        };
        return clsx(BUTTON_STYLE[size].padding, alignStartPaddind[size]);
      }
      return BUTTON_STYLE[size].padding;
    };

    const _gap = () => BUTTON_STYLE[size].gap;

    const _size: { [k in NonNullable<ButtonProps["size"]>]: string } = {
      xs: `${_padding()} ${BUTTON_STYLE.xs.height} ${
        BUTTON_STYLE.xs.radius
      } ${_gap()}`,
      s: `${_padding()} ${BUTTON_STYLE.s.height} ${
        BUTTON_STYLE.s.radius
      } ${_gap()}`,
      m: `${_padding()} ${BUTTON_STYLE.m.height} ${
        BUTTON_STYLE.m.radius
      } ${_gap()}`,
      l: `${_padding()} ${BUTTON_STYLE.l.height} ${
        BUTTON_STYLE.l.radius
      } ${_gap()}`,
    };

    if (!hasText) _size[size] = twMerge(_size[size], BUTTON_STYLE[size].width);

    const spinnerSize = `${
      hasText ? BUTTON_STYLE[size].icon : BUTTON_STYLE[size].iconOnly
    }`;

    const _align = {
      center: `justify-center`,
      start: `justify-start`,
    };

    const childrenContent = analyzedChildren.map(({ child, type }, index) => {
      if (type === "text") {
        if (isValidElement(child)) return child;
        return <ButtonText key={index}>{child}</ButtonText>;
      }

      if (type === "content") {
        return child;
      }

      // type === 'icon'
      let iconElement = child;
      if (!isValidElement(child) || (child as any).type !== ButtonIcon) {
        iconElement = <ButtonIcon key={index}>{child}</ButtonIcon>;
      }

      if (
        align == "start" &&
        isTrailingIcon &&
        index === analyzedChildren.length - 1
      ) {
        return (
          <div
            className={twMerge(
              BUTTON_STYLE[size].width,
              "flex items-center justify-center shrink-0",
              BUTTON_STYLE[size].gapOffsetForTrailingIconWithAlignStart,
              // Overlaps the button's 1px border, so this box occupies the
              // same footprint an icon-only button of this size would — the
              // whole point of giving the trailing icon a box. Confined
              // inside the border it lands a pixel short of where that
              // button's glyph sits, which shows up the moment a select
              // shares a column with one.
              "-mr-px",
            )}
            key={index}
          >
            {iconElement}
          </div>
        );
      }
      return iconElement;
    });

    const isOverlayLoading = loading && !loadingLabel;

    const spinnerOverlay = isOverlayLoading ? (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner className={spinnerSize} />
      </div>
    ) : null;

    const contentToRender =
      loading && loadingLabel ? (
        <>
          <Spinner className={spinnerSize} />
          <ButtonText>{loadingLabel}</ButtonText>
        </>
      ) : (
        childrenContent
      );

    const mergedClassName = twMerge(
      _size[size],
      _variant[variant],
      _align[align],
      variant,
      "relative flex items-center border font-medium select-none shrink-0 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-default",
      className,
    );

    const innerContent = (
      <>
        <div
          className={twMerge(
            "flex items-center flex-auto",
            _gap(),
            _align[align],
            isOverlayLoading && "invisible",
            hasText && "min-w-0",
          )}
        >
          {contentToRender}
        </div>
        {spinnerOverlay}
      </>
    );

    if (asChild && asChildElement) {
      const childProps = asChildElement.props as Record<string, any>;
      const merged: Record<string, any> = {};

      for (const key of Object.keys(childProps)) {
        if (key === "children" || key === "ref") continue;
        merged[key] = childProps[key];
      }

      for (const key of Object.keys(rest)) {
        if (
          key in merged &&
          typeof (rest as any)[key] === "function" &&
          typeof merged[key] === "function" &&
          key.startsWith("on")
        ) {
          const childHandler = merged[key];
          const buttonHandler = (rest as any)[key];
          merged[key] = (...args: any[]) => {
            childHandler(...args);
            buttonHandler(...args);
          };
        } else {
          merged[key] = (rest as any)[key];
        }
      }

      merged.className = twMerge(mergedClassName, childProps.className);
      merged.ref = ref;
      if (loading) merged["aria-busy"] = true;

      return (
        <ButtonContext.Provider value={{ size, variant, hasText, align }}>
          {cloneElement(asChildElement, merged, innerContent)}
        </ButtonContext.Provider>
      );
    }

    return (
      <ButtonContext.Provider value={{ size, variant, hasText, align }}>
        <button
          ref={ref}
          className={mergedClassName}
          disabled={disabled || loading}
          aria-busy={loading || undefined}
          {...rest}
        >
          {innerContent}
        </button>
      </ButtonContext.Provider>
    );
  },
);

ButtonRoot.displayName = "Button";

const ButtonExport = ButtonRoot as unknown as ButtonComponent;
ButtonExport.Icon = ButtonIcon;
ButtonExport.Text = ButtonText;
ButtonExport.Content = ButtonContent;

export const Button = ButtonExport;
export default ButtonExport;
